import { ArticleGridCards } from "@components/Cards/Layout/ArticleGridCards";
import { CompanionAdsLb } from "@elements/Advertisement/variant/CompanionAdsLb";
import fetchTablaApi from "@helper/fetchTablaApi";
import { isArrayWithElements } from "@helper/utils";
import { useTablaCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTablaCustomEventAnalyticsCall";
import {
  CreateLoadMoreHandler,
  CreateLoadMoreHandlerProps,
} from "@hooks/useLoadMore/CreateLoadMoreHandler";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@hooks/useLoadMore/SetFirstRenderedArticleIds";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { AdvertisementTypeEnum, PageAdTargetValue } from "@typings/Ads.d";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import { ReactElement, useState } from "react";

type AuthorPageInfiniteScrollProps = {
  authorArticles: ProcessedArticleData[];
  authorAlias: string;
  pageAdTargetValue: PageAdTargetValue;
  sectionName: string;
  setPageRef: (page: number) => (node: HTMLDivElement | null) => void;
  initialPageCount: number;
};

const PAGE_SIZE = 8;
const INITIAL_ARTICLE_COUNT = 24;

export default function AuthorInfiniteScroll({
  authorArticles,
  authorAlias,
  pageAdTargetValue,
  sectionName,
  setPageRef,
  initialPageCount,
}: AuthorPageInfiniteScrollProps): ReactElement {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const showLoadMore: boolean = authorArticles.length == INITIAL_ARTICLE_COUNT;
  useSetFirstRenderedArticleIds(authorArticles, setRenderedArticleIds);
  const TablaCustomGaEvent = useTablaCustomEventAnalyticsCall();

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length == 0 ? authorArticles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn: authorAlias,
      sort: lastLoadedArticle?.sort,
    };

    const fetchMoreAuthorStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      const queryParams = new URLSearchParams({
        authorUrlAlias: requestData.filterArticlesBasedOn as string,
        sort: Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
      }).toString();
      const response: ProcessedArticleData[] = await fetchTablaApi(
        `more-author-stories?${queryParams}`,
        "GET",
      );

      return response;
    };
    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchMoreAuthorStoriesData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: PAGE_SIZE,
      pageNumber,
    };
    const response = await CreateLoadMoreHandler(createLoadMoreProps);
    if (isArrayWithElements(response)) {
      TablaCustomGaEvent({
        event: "virtual_pv",
        pagination: pageNumber == 0 ? 1 : pageNumber,
      });
    }
    return response;
  };

  const loadedSections = [...loadMoreData].reduce<ProcessedArticleData[][]>(
    (sections, context, index) => {
      if (index % PAGE_SIZE === 0) sections.push([]);
      sections[sections.length - 1].push(context);
      return sections;
    },
    [],
  );

  return (
    <>
      <div className="w-full">
        {loadedSections.map((section, sectionIndex) => (
          <div
            key={sectionIndex}
            id={`page-${sectionIndex + initialPageCount + 1}`}
            ref={setPageRef(sectionIndex + initialPageCount + 1)}
          >
            <CompanionAdsLb
              adType={AdvertisementTypeEnum.LB3}
              uniqueSectionName={sectionName}
              index={sectionIndex + 3}
              pageAdTargetType={pageAdTargetValue}
              rootClassName={
                "mb-5 xmd:my-5 relative w-full bg-grey-950 py-xxm pt-2.5 pb-5 md:px-2 md:py-xxs lg:pt-4 lg:pb-6 lg:px-0"
              }
            />
            <ArticleGridCards
              article={section}
              articleIndex={sectionIndex}
              rootClassName="px-3 xl:p-0 gap-3 md:inline-grid md:grid-cols-4 xmd:inline-grid lg:grid xmd:pb-10 lg:max-w-7xl bg-white m-auto"
              cardWrapper="gap-2 xmd:py-0 md:flex-col md:max-w-[210px] xmd:max-w-full"
              imageWrapperStyle="md:max-w-[171px] xmd:w-full"
            />
          </div>
        ))}
        {showLoadMore ? (
          <div className="flex w-full justify-center mb-xl px-3 xl:px-0 max-w-7xl m-auto">
            <LoadMore
              rootClassName="my-4 w-full"
              onLoadMore={handleLoadMore}
              hasMore={hasMoreStories}
              loadText="Load More"
              mobileInfiniteScroll={true}
              loadDefaultGaEvent={false}
            />
          </div>
        ) : null}
      </div>
    </>
  );
}
